import localForage from "localforage";
import _ from "lodash";

import baseApi from "@/api";
import EventBus from "@/utils/event-bus";
import authApi from "./api";
import authInterceptor from "./interceptor";
import { requireAuth, skipIfAuth } from "./guards";

const ACCESS_TOKEN_KEY = "access_token";

export default class AuthService {
  static install = function() {
    return undefined;
  };

  _accessToken = undefined;

  initProc = undefined;
  tokenRefreshProc = undefined;

  constructor(options) {
    options = options || {};
    this.tokenExpirationDelta = options.tokenExpirationDelta || 60 * 60;
    this.tokenRefreshExpirationDelta =
      options.tokenRefreshExpirationDelta || 7 * 24 * 60 * 60;
    this.leeway = options.leeway || 60;

    this.isInitialized = false;
    this.initProc = this.init().then(() => {
      this.isInitialized = true;
    });
  }

  async init() {
    const token = await localForage.getItem(ACCESS_TOKEN_KEY);

    if (token) {
      try {
        await authApi.verifyToken(token);
      } catch (e) {
        await localForage.removeItem(ACCESS_TOKEN_KEY);
      }

      try {
        const response = await authApi.refreshToken(token);

        const newToken = _.get(response, "data.token");
        await this.setToken(newToken);

        this.startTokenRefresh();
      } catch (e) {
        await this.lock();
      }
    }
  }

  async setToken(token) {
    try {
      await localForage.setItem(ACCESS_TOKEN_KEY, token);
    } finally {
      this._accessToken = token;
    }
  }

  get accessToken() {
    return this._accessToken;
  }

  get isAuthenticated() {
    if (!this.isInitialized) return;
    return !!this._accessToken;
  }

  async login(credentials) {
    try {
      const response = await authApi.obtainToken(credentials);
      const token = _.get(response, "data.token");
      await this.setToken(token);
    } catch (e) {
      throw e;
    }

    EventBus.$emit("auth::logged-in");

    this.startTokenRefresh();
  }

  async lock() {
    this.stopTokenRefresh();

    try {
      await localForage.removeItem(ACCESS_TOKEN_KEY);
    } finally {
      this._accessToken = undefined;
    }

    EventBus.$emit("auth::locked");
  }

  async logout() {
    this.stopTokenRefresh();

    try {
      await localForage.clear();
    } finally {
      this._accessToken = undefined;
    }

    EventBus.$emit("auth::logged-out");
  }

  startTokenRefresh() {
    if (this.tokenRefreshProc) {
      clearInterval(this.tokenRefreshProc);
    }

    this.tokenRefreshProc = setInterval(() => {
      if (!this._accessToken) {
        clearInterval(this.tokenRefreshProc);
        return;
      }

      authApi
        .refreshToken(this._accessToken)
        .then(res => {
          const newToken = _.get(res, "data.token");

          this.setToken(newToken).then();
        })
        .catch(() => {
          this.lock().then();
        });
    }, this.tokenExpirationDelta * 1000);
  }

  stopTokenRefresh() {
    if (!this.tokenRefreshProc) return;

    clearInterval(this.tokenRefreshProc);
  }
}

AuthService.install = function(Vue, options) {
  if (AuthService.install.installed) return;
  AuthService.install.installed = true;

  const authService = new AuthService(options);

  Vue.auth = authService;
  Object.defineProperty(Vue.prototype, "$auth", {
    get() {
      return authService;
    }
  });

  baseApi.interceptors.request.use(authInterceptor);
};

export { requireAuth, skipIfAuth };
